/**
 * Site-wide configuration: identity, navigation, contact details, socials.
 *
 * TODO: markers below are intentional and fail `npm run check:content`. They
 * mark real information only the Softife team can supply (SPEC.md §20).
 */

export type SiteConfig = {
  name: string
  tagline: string
  description: string
  contact: {
    email: string
    phone: string
    /**
     * City and country are the minimum that makes the Organization schema
     * useful for brand/locality recognition. The rest are optional so a
     * partial-but-true address can ship — a street address must never be
     * invented to satisfy the type.
     */
    address: null | {
      city: string
      country: string
      street?: string
      region?: string
      postalCode?: string
    }
    responseTime: string
  }
  socials: { name: string; url: string }[]
  values: { title: string; body: string }[]
  process: { title: string; body: string }[]
}

export const site: SiteConfig = {
  name: 'Softife',
  tagline: 'Smart Technology Solutions',
  /** The elevator pitch — what Softife does, for whom. Not the tagline. */
  description:
    'Softife designs and builds custom software that helps businesses streamline operations and stay ahead — web and mobile applications, enterprise systems, and cloud infrastructure.',

  /** Verified contact details. TODO: confirm before launch. */
  contact: {
    email: 'TODO: real contact email',
    phone: '', // Optional. Leave empty rather than inventing one.
    /**
     * A real street address enables LocalBusiness schema. Omit if none.
     * City + country only for now — enough for Organization/PostalAddress,
     * not enough for LocalBusiness. Add street/region/postalCode when the
     * team confirms a real registered address.
     */
    address: {
      city: 'Dhaka',
      country: 'BD',
    },
    /** Only state a response time the team will actually meet. */
    responseTime: 'TODO: confirm response-time commitment, or remove',
  },

  /**
   * Used for `sameAs` in Organization structured data, and rendered as footer
   * links. Empty = omitted from both.
   *
   * >>> EDIT HERE to feed `sameAs`. Uncomment a line and replace the URL with
   * the real profile. Each entry must be a live, publicly reachable page that
   * visibly belongs to Softife — `sameAs` is how Google corroborates that the
   * brand is real, so a dead or wrong URL weakens the signal it is meant to
   * provide. Uncommenting also adds a link to the footer.
   */
  socials: [
    // { name: 'LinkedIn', url: 'https://www.linkedin.com/company/REPLACE_ME' },
    // { name: 'Facebook', url: 'https://www.facebook.com/REPLACE_ME' },
    // { name: 'GitHub', url: 'https://github.com/REPLACE_ME' },
  ],

  /** Company values, framed as client benefits rather than adjectives. */
  values: [
    {
      title: 'Innovation',
      body: 'We keep pace with the tooling that actually matters, so you get solutions built on current technology rather than whatever was standard five years ago.',
    },
    {
      title: 'Reliability',
      body: 'Software you can depend on: tested, documented, and built to be maintained after launch — not just delivered.',
    },
    {
      title: 'Client-Centric Approach',
      body: 'We start from your problem, not from a template. What we build is shaped by how your business actually works.',
    },
    {
      title: 'Continuous Growth',
      body: 'Technology moves; so do we. We keep learning so the systems we build for you stay current.',
    },
  ],

  /** How engagements run. Reduces "what am I actually buying" friction. */
  process: [
    {
      title: 'Discover',
      body: 'We learn your business, the problem, and the constraints — before proposing anything.',
    },
    {
      title: 'Design',
      body: 'Flows, architecture, and interface decisions, agreed with you before code is written.',
    },
    {
      title: 'Build',
      body: 'Iterative delivery with working software you can review, not a six-month black box.',
    },
    {
      title: 'Support',
      body: 'Handover, documentation, and ongoing support so the system keeps working.',
    },
  ],
}

export const primaryCta = { label: 'Start a project', href: '/contact' } as const
export const secondaryCta = { label: 'See our work', href: '/work' } as const

export type NavItem = { label: string; href: string }

/** Footer link groups. Service links are generated from content at render time. */
export const footerLinks: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Company',
    items: [
      { label: 'About', href: '/about' },
      { label: 'Our work', href: '/work' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    heading: 'Legal',
    items: [
      { label: 'Privacy policy', href: '/privacy' },
      { label: 'Terms of service', href: '/terms' },
    ],
  },
]
